import * as Sentry from '@sentry/react';

import { Modals, Regions } from '@/constants';
import {
  fetchContentPreferences,
  fetchPrimaryRegion,
  fetchUserUIStates,
  PostgrestEndpoints,
  setRegionPreference,
  submitProductFeedback,
  updateUserDesignationsRequest,
  updateUserUIState,
} from '@/requests';
import { AnalyticsService, EVENTS } from '@/services';
import {
  MemberRole,
  Plan,
  Region,
  SubmitProductFeedbackPayload,
  UserDesignation,
  UserProfile,
} from '@/types';
import { camelCaseKeys } from '@/utils';

import { onboardingApiPostgrest } from '../onboarding-api';
import { postgrestApi } from '../postgrest-api';
import { RootState } from '../store';
import { PostgrestApiTags } from '../store.constants';
import {
  handleOnQueryStartedError,
  rtkQueryError,
  transformAuthErrorResponse,
} from '../store.utils';
import { uiActions } from '../ui';
import { RecoveryEmailErrorCodes } from './user-api.constants';
import {
  transformDesignationsFormForApi,
  transformPlan,
} from './user-api.utils';

type UpdateUserDesignationsPayload = {
  userCountry: string;
  userDesignations: Partial<
    Record<
      UserDesignation['countryCode'],
      Partial<Record<UserDesignation['designation'], unknown>>
    >
  >;
};

type UpdateUserProfilePayload = {
  firstName?: string;
  lastName?: string;
  email?: string;
  jobTitle?: string;
  companyName?: string;
  event?: Record<'INTENT' | 'SUCCESS' | 'FAIL', string>;
};

type UpdatePasswordPayload = {
  oldPassword: string;
  newPassword: string;
  email: string;
  event?: Record<'INTENT' | 'SUCCESS' | 'FAIL', string>;
};

type CompleteAccountRecoveryResponse = {
  status: 'succeeded' | 'failed';
  message: string;
};

type ValidRecoveryVerificationCodeResponse = {
  valid: boolean;
  identity_email: string;
  recovery_email: string;
};

type FetchRecoveryEmailResponse = {
  recoveryEmail: string;
  verificationStatus: 'verified' | 'unverified' | 'not_attempted';
};

type UserUIStates = {
  isTrackerNewFeaturesModalDismissed: boolean;
  isLibraryUpdateDismissed: boolean;
  onboardingChecklistProgress: {
    currentStep: number;
    isChecklistCompleted: boolean;
  };
  pinnedFieldsOfStudy: string[];
};

type UpdateUserUIStatePayload = {
  [K in keyof UserUIStates]: {
    key: K;
    value: UserUIStates[K];
  };
}[keyof UserUIStates];

const assignUserUIState = <K extends keyof UserUIStates>(
  draft: UserUIStates,
  payload: { key: K; value: UserUIStates[K] },
) => {
  draft[payload.key] = payload.value;
};

export const userApiPostgrest = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    fetchUserProfile: builder.query<UserProfile, void>({
      query: () => ({
        url: PostgrestEndpoints.PROFILE,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        return {
          firstName: response[0]?.first_name,
          lastName: response[0]?.last_name,
          email: response[0]?.email,
          onboardingCompletedAt: response[0]?.onboarding_completed_at,
          userCreatedAt: response[0]?.user_created_at,
          jobTitle: response[0]?.job_title,
          companyName: response[0]?.company_name,
          memberRoles: camelCaseKeys<MemberRole[]>(response[0]?.member_roles),
        };
      },
      providesTags: [PostgrestApiTags.USER_PROFILE],
    }),
    fetchUserRegistrationState: builder.query<boolean, void>({
      query: () => ({
        url: PostgrestEndpoints.USER_REGISTRATION_STATE,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: isRegistrationCompleted } = await queryFulfilled;
          Sentry.setContext('onboarding', { isRegistrationCompleted });
          if (isRegistrationCompleted) return;
          dispatch(uiActions.setActiveModal({ name: Modals.ONBOARDING }));
          AnalyticsService.logEvent(EVENTS.ONBOARDING_EVENTS.START);
        } catch (error) {
          handleOnQueryStartedError(error);
        }
      },
      transformResponse: (response: any) =>
        !!response[0]?.registration_completed,
      providesTags: [PostgrestApiTags.USER_REGISTRATION_STATE],
    }),
    updateUserDesignations: builder.mutation<
      UserDesignation[],
      UpdateUserDesignationsPayload
    >({
      queryFn: async (
        { userCountry, userDesignations },
        { getState },
        _,
        baseQuery,
      ) => {
        try {
          const { data: designationOptions } =
            onboardingApiPostgrest.endpoints.fetchDesignationOptions.select()(
              getState() as RootState,
            );

          const payload = transformDesignationsFormForApi({
            userCountry,
            userDesignations,
            designationOptions,
          });

          const { data: designationResults, ...result } = await baseQuery(
            updateUserDesignationsRequest({ userDesignations: payload }),
          );

          if (result.error) {
            return { data: undefined, error: result.error, meta: result.meta };
          }

          return {
            data: camelCaseKeys<UserDesignation[]>(designationResults),
          };
        } catch (error) {
          return rtkQueryError(error);
        }
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          AnalyticsService.logEvent(
            EVENTS.ONBOARDING_EVENTS.UPDATE_DESIGNATION.INTENT,
          );
          const { data } = await queryFulfilled;
          AnalyticsService.logEvent(
            EVENTS.ONBOARDING_EVENTS.UPDATE_DESIGNATION.SUCCESS,
            {
              designations: data.map(d => d.type),
            },
          );
        } catch (error: any) {
          AnalyticsService.logEvent(
            EVENTS.ONBOARDING_EVENTS.UPDATE_DESIGNATION.FAIL,
            {
              error: error?.message,
            },
          );
          handleOnQueryStartedError(error);
        }
      },
    }),
    fetchPrimaryRegion: builder.query<Region | undefined, void>({
      query: fetchPrimaryRegion,
      transformResponse: (response: { designations: string }[] | undefined) => {
        const region = response?.[0]?.designations;
        if (!region) return undefined;
        if (!Regions[region as Region]) {
          return Regions.CAN;
        }
        return region as Region;
      },
      providesTags: [PostgrestApiTags.USER_REGION],
    }),
    fetchContentPreferences: builder.query<
      { audienceRegions?: string[] },
      void
    >({
      query: fetchContentPreferences,
      transformResponse: (response: any) => ({
        audienceRegions: response?.['audience-region'],
      }),
      providesTags: [PostgrestApiTags.CONTENT_PREFERENCES],
    }),
    setRegionPreference: builder.mutation<any, { regionSlugs: string[] }>({
      query: setRegionPreference,
      invalidatesTags: [
        PostgrestApiTags.CONTENT_PREFERENCES,
        PostgrestApiTags.FEATURED_EPISODES,
        PostgrestApiTags.TRENDING_EPISODES,
        PostgrestApiTags.NEW_EPISODES,
        PostgrestApiTags.CATEGORY_EPISODES,
        PostgrestApiTags.SEARCH_RESULTS,
      ],
    }),
    fetchUserUIStates: builder.query<UserUIStates, void>({
      query: fetchUserUIStates,
      transformResponse: (response: any) => response?.data ?? {},
    }),
    updateUserUIState: builder.mutation<void, UpdateUserUIStatePayload>({
      query: updateUserUIState,
      onQueryStarted: async ({ key, value }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            userApiPostgrest.util.updateQueryData(
              'fetchUserUIStates',
              undefined,
              draft => {
                assignUserUIState(draft, { key, value });
              },
            ),
          );
        } catch (error) {
          handleOnQueryStartedError(error);
        }
      },
    }),
    fetchPlan: builder.query<Plan, void>({
      query: () => ({
        url: PostgrestEndpoints.PLAN,
      }),
      transformResponse: transformPlan,
      providesTags: [PostgrestApiTags.PLAN],
    }),
    updateUserProfile: builder.mutation<void, UpdateUserProfilePayload>({
      query: ({ firstName, lastName, email, jobTitle, companyName }) => ({
        method: 'POST',
        url: PostgrestEndpoints.UPDATE_PROFILE,
        body: {
          first_name: firstName,
          last_name: lastName,
          job_title: jobTitle || null,
          company_name: companyName || null,
          email,
        },
      }),
      onQueryStarted: async (
        { firstName, lastName, jobTitle, companyName, email, event },
        { queryFulfilled },
      ) => {
        if (!event) return;

        try {
          if (event === EVENTS.PROFILE_EVENTS.UPDATE_EMAIL) {
            AnalyticsService.logEvent(event.INTENT, { newEmail: email || '' });
          } else {
            AnalyticsService.logEvent(event.INTENT, {
              firstName: firstName || '',
              lastName: lastName || '',
              jobTitle: jobTitle || '',
              companyName: companyName || '',
            });
          }
          await queryFulfilled;
          AnalyticsService.logEvent(event.SUCCESS);
        } catch (error: any) {
          AnalyticsService.logEvent(event.FAIL, {
            error: error?.message,
          });
          handleOnQueryStartedError(error);
        }
      },
      invalidatesTags: [PostgrestApiTags.USER_PROFILE],
    }),
    fetchUserTags: builder.query<string[], void>({
      query: () => ({
        url: PostgrestEndpoints.USER_TAGS,
      }),
      transformResponse: (response: any) => response.tags,
      providesTags: [PostgrestApiTags.USER_TAGS],
    }),
    showProductFeedback: builder.query<boolean, void>({
      query: () => ({
        url: PostgrestEndpoints.SHOW_PRODUCT_FEEDBACK,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        return response && response.length
          ? response[0].show_product_feedback
          : undefined;
      },
      providesTags: [PostgrestApiTags.SHOW_PRODUCT_FEEDBACK],
    }),
    submitProductFeedback: builder.mutation<void, SubmitProductFeedbackPayload>(
      {
        query: submitProductFeedback,
        onQueryStarted: async (_, { queryFulfilled }) => {
          try {
            AnalyticsService.logEvent(
              EVENTS.PRODUCT_FEEDBACK_EVENTS.SUBMIT.INTENT,
            );

            await queryFulfilled;

            AnalyticsService.logEvent(
              EVENTS.PRODUCT_FEEDBACK_EVENTS.SUBMIT.SUCCESS,
            );
          } catch (error: any) {
            AnalyticsService.logEvent(
              EVENTS.PRODUCT_FEEDBACK_EVENTS.SUBMIT.FAIL,
              {
                error: error?.message,
              },
            );
            handleOnQueryStartedError(error);
          }
        },
        invalidatesTags: [PostgrestApiTags.SHOW_PRODUCT_FEEDBACK],
      },
    ),
    updatePassword: builder.mutation<boolean, UpdatePasswordPayload>({
      query: ({ oldPassword, newPassword, email }) => ({
        method: 'POST',
        url: PostgrestEndpoints.UPDATE_PASSWORD,
        body: {
          email,
          old_password: oldPassword,
          new_password: newPassword,
        },
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          AnalyticsService.logEvent(
            EVENTS.PROFILE_EVENTS.UPDATE_PASSWORD.INTENT,
          );
          await queryFulfilled;
          AnalyticsService.logEvent(
            EVENTS.PROFILE_EVENTS.UPDATE_PASSWORD.SUCCESS,
          );
        } catch (error: any) {
          AnalyticsService.logEvent(
            EVENTS.PROFILE_EVENTS.UPDATE_PASSWORD.FAIL,
            {
              error: error?.message,
            },
          );
          handleOnQueryStartedError(error);
        }
      },
    }),
    linkPlanToUser: builder.mutation<void, { inviteToken: string }>({
      query: ({ inviteToken }) => ({
        method: 'POST',
        url: PostgrestEndpoints.LINK_PLAN,
        body: { invite_token: inviteToken },
      }),
      invalidatesTags: [PostgrestApiTags.PLAN, PostgrestApiTags.USER_TAGS],
    }),
    addRecoveryEmail: builder.mutation<void, { recoveryEmail: string }>({
      query: ({ recoveryEmail }) => ({
        method: 'POST',
        url: PostgrestEndpoints.ADD_RECOVERY_EMAIL,
        body: { recovery_email: recoveryEmail },
      }),
      transformErrorResponse: error =>
        transformAuthErrorResponse(error, RecoveryEmailErrorCodes),
      invalidatesTags: [PostgrestApiTags.RECOVERY_EMAIL],
    }),
    fetchRecoveryEmail: builder.query<FetchRecoveryEmailResponse, void>({
      query: () => ({
        url: PostgrestEndpoints.FETCH_RECOVERY_EMAIL,
        method: 'GET',
      }),
      transformResponse: (response: any) => camelCaseKeys(response),
      providesTags: [PostgrestApiTags.RECOVERY_EMAIL],
    }),
    verifyRecoveryEmail: builder.mutation<void, { verificationCode: string }>({
      query: ({ verificationCode }) => ({
        method: 'POST',
        url: PostgrestEndpoints.VERIFY_RECOVERY_EMAIL,
        body: { verification_code: verificationCode },
      }),
      transformErrorResponse: error =>
        transformAuthErrorResponse(error, RecoveryEmailErrorCodes),
      invalidatesTags: [PostgrestApiTags.RECOVERY_EMAIL],
    }),
    removeRecoveryEmail: builder.mutation<void, void>({
      query: () => ({
        method: 'POST',
        url: PostgrestEndpoints.REMOVE_RECOVERY_EMAIL,
      }),
      transformErrorResponse: error =>
        transformAuthErrorResponse(error, RecoveryEmailErrorCodes),
      invalidatesTags: [PostgrestApiTags.RECOVERY_EMAIL],
    }),
    validateRecoveryVerificationCode: builder.query<
      ValidRecoveryVerificationCodeResponse,
      { verificationCode: string }
    >({
      query: ({ verificationCode }) => ({
        url: PostgrestEndpoints.VALIDATE_VERIFICATION_CODE,
        method: 'POST',
        body: {
          verification_code: verificationCode,
        },
      }),
    }),
    completeAccountRecovery: builder.mutation<
      CompleteAccountRecoveryResponse,
      { password: string; verificationCode: string }
    >({
      query: ({ password, verificationCode }) => ({
        url: PostgrestEndpoints.COMPLETE_ACCOUNT_RECOVERY,
        method: 'POST',
        body: {
          password,
          verification_code: verificationCode,
        },
      }),
    }),
  }),
});

export const {
  useFetchUserProfileQuery,
  useUpdateUserProfileMutation,
  useFetchUserRegistrationStateQuery,
  useFetchPlanQuery,
  useUpdateUserDesignationsMutation,
  useFetchUserTagsQuery,
  useUpdatePasswordMutation,
  useSubmitProductFeedbackMutation,
  useShowProductFeedbackQuery,
  useLinkPlanToUserMutation,
  useAddRecoveryEmailMutation,
  useFetchRecoveryEmailQuery,
  useVerifyRecoveryEmailMutation,
  useRemoveRecoveryEmailMutation,
  useValidateRecoveryVerificationCodeQuery,
  useCompleteAccountRecoveryMutation,
  useFetchPrimaryRegionQuery,
  useFetchUserUIStatesQuery,
  useUpdateUserUIStateMutation,
  useFetchContentPreferencesQuery,
  useSetRegionPreferenceMutation,
} = userApiPostgrest;
